import "./avf-dashboard.scss";
import React, { useState, useEffect } from "react";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column, ColumnBodyOptions, ColumnFilterElementTemplateOptions } from "primereact/column";

import DateUtils from "../../utils/helpers/date.helper";
import { loadData } from "./services";
import { AvfDataView } from "./models";
import { FilterMatchMode } from "primereact/api";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";

const AvfDashboardReport = () => {
  const [dataView, setDataView] = useState<AvfDataView[]>([]);
  const [statesFilterOpts, setStatesFilterOpts] = useState<string[]>([]);
  const [sitesFilterOpts, setSitesFilterOpts] = useState<string[]>([]);
  const statusFilterOpts: string[] = ["Review Pending", "Approved", "Rejected"];
  const [reqTypeFilterOpts, setReqTypeFilterOpts] = useState<string[]>([
    "Facilities",
    "IT Cloud",
    "NEM",
    "Network",
    "Other",
    "PTF",
    "RSP-MSB-Facilities Access",
  ]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dhDocID: { value: null, matchMode: FilterMatchMode.CONTAINS },
    state: { value: null, matchMode: FilterMatchMode.IN },
    site: { value: null, matchMode: FilterMatchMode.IN },
    "request Type": { value: null, matchMode: FilterMatchMode.IN },
    applicant: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.IN },
    "impairment?": { value: null, matchMode: FilterMatchMode.EQUALS },
    "daily Reinstate?": { value: null, matchMode: FilterMatchMode.EQUALS },
    "high Risk?": { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const loadDashboard = async () => {
    try {
      const data = await loadData();
      if (data) {
        setDataView(data);
        let stateSet = new Set(data.map((x) => x.state));
        setStatesFilterOpts(Array.from(stateSet).sort());
        let siteSet = new Set(data.map((x) => x.site));
        setSitesFilterOpts(Array.from(siteSet).sort());
        let reqTypeSet = new Set([...reqTypeFilterOpts, ...data.map((x) => x["request Type"])]);
        setReqTypeFilterOpts(Array.from(reqTypeSet).sort());
      }
    } catch (ex) {
      console.error(ex);
      alert(ex);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const openLinkTemplate = (data: AvfDataView, _: ColumnBodyOptions): JSX.Element => {
    return (
      <span title="Open Form">
        <a
          href={`https://autotechfm.visualvault.com/App/NBNCo/Main/formdetails?DataID=${data.dhid}&hidemenu=true`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          {data.dhDocID} <i className="pi pi-external-link" style={{ fontSize: "0.8rem" }}></i>
        </a>
      </span>
    );
  };

  const dateFieldTemplate = (data: AvfDataView, col: ColumnBodyOptions): JSX.Element => {
    const stringDate: string = (data as any)[col.field];
    const date = DateUtils.formatDateStringShort(stringDate);
    return <span>{date ?? ""}</span>;
  };

  const impairmentTemplate = (data: AvfDataView, col: ColumnBodyOptions): JSX.Element => {
    const isImpaired = (data as any)[col.field] === "Yes";

    if (isImpaired) {
      return (
        <div className="conditional-cell cell-danger">
          <span>Yes</span>
        </div>
      );
    } else {
      return (
        <div className="conditional-cell cell-success">
          <span>{(data as any)[col.field]}</span>
        </div>
      );
    }
  };

  const dailyReinstateTemplate = (data: AvfDataView, col: ColumnBodyOptions): JSX.Element => {
    const notReinstated = (data as any)[col.field] === "No";

    if (notReinstated && data["impairment?"] === "Yes") {
      return (
        <div className="conditional-cell cell-danger">
          <span>No</span>
        </div>
      );
    } else if (data["impairment?"] === "Yes") {
      return (
        <div className="conditional-cell cell-success">
          <span>{(data as any)[col.field]}</span>
        </div>
      );
    }

    return (
      <div className="conditional-cell">
        <span>-</span>
      </div>
    );
  };

  const highRiskTemplate = (data: AvfDataView, col: ColumnBodyOptions): JSX.Element => {
    const isHighRisk = (data as any)[col.field] === "True";
    if (isHighRisk) {
      return (
        <div className="conditional-cell cell-danger">
          <span>Yes</span>
        </div>
      );
    } else {
      return (
        <div className="conditional-cell cell-success">
          <span>No</span>
        </div>
      );
    }
  };

  const statusTemplate = (data: AvfDataView, col: ColumnBodyOptions): JSX.Element => {
    const status: string = (data as any)[col.field] || "Review Pending";
    let className = "conditional-cell";
    switch (status) {
      case "Approved":
        className += " cell-success";
        break;
      case "Review Pending":
      case "":
        className += " cell-warning";
        break;
      default:
        className += " cell-disabled";
        break;
    }
    return (
      <div className={className}>
        <span>{status}</span>
      </div>
    );
  };

  const filterStatusTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return <MultiSelectFilter label="Status" options={statusFilterOpts} col={options} />;
  };

  const filterStateTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return <MultiSelectFilter label="State" options={statesFilterOpts} col={options} />;
  };

  const filterSiteTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return <MultiSelectFilter label="Site" options={sitesFilterOpts} col={options} />;
  };

  const filterReqTypeTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return <MultiSelectFilter label="Type" options={reqTypeFilterOpts} col={options} />;
  };

  const filterImpairmentTemplate = (options: ColumnFilterElementTemplateOptions) => {
    const opts = ["Yes", "No"];
    return <DropdownFilter label="Select" options={opts} col={options} />;
  };

  const filterHighRiskTemplate = (options: ColumnFilterElementTemplateOptions) => {
    const opts = ["True", "False"];
    return <DropdownFilter label="Select" options={opts} col={options} />;
  };

  const MultiSelectFilter = (props: { label: string; options: string[]; col: ColumnFilterElementTemplateOptions }) => {
    return (
      <MultiSelect
        value={props.col.value}
        options={props.options}
        onChange={(e) => props.col.filterApplyCallback(e.value)}
        placeholder={props.label}
        className="p-column-filter"
        showClear={false}
        style={{ width: "100%" }}
      />
    );
  };

  const DropdownFilter = (props: { label: string; options: string[]; col: ColumnFilterElementTemplateOptions }) => {
    return (
      <Dropdown
        value={props.col.value}
        options={props.options}
        onChange={(e) => props.col.filterApplyCallback(e.value)}
        placeholder={props.label}
        className="p-column-filter"
        showClear={false}
        style={{ width: "100%" }}
      />
    );
  };

  return (
    <React.Fragment>
      <div className="avf-dashboard-wrapper">
        <DataTable
          value={dataView}
          key={"dhDocID"}
          stateStorage="local"
          stateKey="09AeHb-state-save-local"
          //
          filters={filters}
          filterDisplay="row"
          //
          loading={loading}
          //
          scrollable
          scrollHeight="flex"
          stripedRows
          rowHover
          size="small"
          //
          paginator
          rows={50}
          rowsPerPageOptions={[25, 50, 100, 200]}
        >
          <Column
            field="dhDocID"
            header="Form ID"
            body={openLinkTemplate}
            sortable
            filter
            filterPlaceholder="Search"
            style={{ minWidth: "12rem" }}
          ></Column>
          <Column field="submitted" header="Submitted" sortable body={dateFieldTemplate}></Column>
          <Column
            field="state"
            header="State"
            sortable
            filter
            filterElement={filterStateTemplate}
            showFilterMenu={false}
            style={{ maxWidth: "10rem" }}
          ></Column>
          <Column
            field="site"
            header="Site"
            sortable
            filter
            filterElement={filterSiteTemplate}
            showFilterMenu={false}
            style={{ minWidth: "12rem" }}
          ></Column>
          <Column
            field="request Type"
            header="Request Type"
            sortable
            filter
            filterElement={filterReqTypeTemplate}
            showFilterMenu={false}
            style={{ minWidth: "10rem" }}
          ></Column>
          <Column
            field="applicant"
            header="Applicant"
            sortable
            filter
            filterPlaceholder="Search"
            style={{ minWidth: "12rem" }}
          ></Column>
          <Column field="description" header="Description" sortable style={{ minWidth: "20rem" }}></Column>
          <Column
            field="scheduled"
            header="Scheduled"
            sortable
            body={dateFieldTemplate}
            style={{ minWidth: "8rem" }}
          ></Column>
          <Column field="start" header="Start" style={{ minWidth: "5rem" }}></Column>
          <Column field="to" header="To" sortable body={dateFieldTemplate} style={{ minWidth: "8rem" }}></Column>
          <Column field="end" header="End" style={{ minWidth: "5rem" }}></Column>
          <Column
            field="impairment?"
            header="Impairment?"
            sortable
            body={impairmentTemplate}
            className="conditional-cell-wrapper"
            filter
            filterElement={filterImpairmentTemplate}
            showFilterMenu={false}
            style={{ minWidth: "9rem" }}
          ></Column>
          <Column
            field="daily Reinstate?"
            header="Daily Reinstate?"
            sortable
            body={dailyReinstateTemplate}
            className="conditional-cell-wrapper"
            filter
            filterElement={filterImpairmentTemplate}
            showFilterMenu={false}
            style={{ minWidth: "9rem" }}
          ></Column>
          <Column
            field="high Risk?"
            header="High Risk?"
            sortable
            body={highRiskTemplate}
            className="conditional-cell-wrapper"
            filter
            filterElement={filterHighRiskTemplate}
            showFilterMenu={false}
            style={{ minWidth: "9rem" }}
          ></Column>
          <Column
            field="status"
            header="Status"
            sortable
            body={statusTemplate}
            className="conditional-cell-wrapper"
            filter
            filterElement={filterStatusTemplate}
            showFilterMenu={false}
            style={{ minWidth: "10rem" }}
          ></Column>
          <Column field="signed off by" header="Signed off by" sortable style={{ minWidth: "12rem" }}></Column>
          <Column
            field="site Instruction (if approved)"
            header="Site Instruction (if approved)"
            sortable
            style={{ minWidth: "15rem" }}
          ></Column>
          <Column
            field="(or) Rejection Reason"
            header="(or) Rejection Reason"
            sortable
            style={{ minWidth: "15rem" }}
          ></Column>
        </DataTable>
      </div>
    </React.Fragment>
  );
};

export default AvfDashboardReport;
